/**
 * useContentPlanFlowReadiness — live D12 flow-readiness for the M2 wizard.
 *
 * Gathers `ContentPlanReadinessCounts` client-side from the module's existing
 * subscriptions (authors, platforms, pillars, backlog, strategies,
 * publications of the newest strategy, canonical posts) and maps them through
 * THE shared predicate implementation `evaluateContentPlanFlowReadiness`
 * (`@modules/shared-types`) — the same function `content-plan doctor` uses.
 * Step logic is NEVER re-implemented here (D12 parity contract; see
 * `utils/contentPlanFlowReadiness.ts` + its parity test).
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import type {
  CliContentPlanFlowReadinessData,
  ContentPlanReadinessCounts,
} from '@modules/shared-types';
import { useAuthorList } from './useAuthor';
import { useAuthorPlatforms } from './useAuthorPlatforms';
import { usePillars } from './usePillars';
import { useTopicBacklog } from './useTopicBacklog';
import { useContentPublications } from './useContentPublications';
import { ContentStrategyFirestoreService } from '../services/ContentStrategyFirestoreService';
import { contentPostFirestoreService } from '../services/ContentPostFirestoreService';
import type { ContentStrategy } from '../types';
import {
  computeContentPlanFlowReadiness,
  gatherContentPlanReadinessCounts,
  pickNewestStrategyId,
  resolveReadinessAuthorId,
  type ContentPlanReadinessSources,
} from '../utils/contentPlanFlowReadiness';

export interface UseContentPlanFlowReadinessReturn {
  /** `{ ready, steps }` straight from the shared D12 evaluator. */
  readiness: Pick<CliContentPlanFlowReadinessData, 'ready' | 'steps'>;
  /** The raw observed counts the evaluator consumed (for card status lines). */
  counts: ContentPlanReadinessCounts;
  /** Author the counts are scoped to (workspace author → default → sole). */
  effectiveAuthorId: string | null;
  loading: boolean;
  /** Force a resubscribe of the manually-managed listeners. */
  refresh: () => void;
}

export function useContentPlanFlowReadiness(
  projectId: string | null,
  activeAuthorId?: string | null,
): UseContentPlanFlowReadinessReturn {
  const [refreshNonce, setRefreshNonce] = useState(0);
  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  // ── Live sources (existing module subscriptions) ──
  const { authors, loading: authorsLoading } = useAuthorList(projectId);
  const effectiveAuthorId = useMemo(
    () => resolveReadinessAuthorId(authors, activeAuthorId ?? null),
    [authors, activeAuthorId],
  );

  const { platforms, loading: platformsLoading } = useAuthorPlatforms(projectId, effectiveAuthorId);
  const { pillars, loading: pillarsLoading } = usePillars(projectId);
  const { topics, loading: topicsLoading } = useTopicBacklog(projectId, effectiveAuthorId);

  // Strategies — service subscription (same one StrategiesTable uses).
  const [strategies, setStrategies] = useState<ContentStrategy[]>([]);
  const [strategiesLoading, setStrategiesLoading] = useState(true);
  useEffect(() => {
    if (!projectId) {
      setStrategies([]);
      setStrategiesLoading(false);
      return;
    }
    setStrategiesLoading(true);
    const service = new ContentStrategyFirestoreService();
    const unsub = service.subscribeStrategies(projectId, effectiveAuthorId, (list) => {
      setStrategies(list);
      setStrategiesLoading(false);
    });
    return () => unsub();
  }, [projectId, effectiveAuthorId, refreshNonce]);

  const newestStrategyId = useMemo(() => pickNewestStrategyId(strategies), [strategies]);

  // Publications of the newest strategy only (D12 publications predicate).
  const { publications, loading: publicationsLoading } = useContentPublications(
    projectId,
    newestStrategyId,
  );

  // Canonical posts for the effective author.
  const [postCount, setPostCount] = useState(0);
  const [postsLoading, setPostsLoading] = useState(true);
  useEffect(() => {
    if (!projectId) {
      setPostCount(0);
      setPostsLoading(false);
      return;
    }
    setPostsLoading(true);
    const unsub = contentPostFirestoreService.subscribe(
      projectId,
      { authorId: effectiveAuthorId },
      (posts) => {
        setPostCount(posts.length);
        setPostsLoading(false);
      },
      () => setPostsLoading(false),
    );
    return () => unsub();
  }, [projectId, effectiveAuthorId, refreshNonce]);

  // ── Gather + evaluate through the shared D12 contract ──
  const sources = useMemo<ContentPlanReadinessSources>(() => ({
    authors,
    platformCount: platforms.length,
    pillars,
    topics,
    strategies,
    newestStrategyPublicationCount: publications.length,
    postCount,
    activeAuthorId: activeAuthorId ?? null,
  }), [authors, platforms, pillars, topics, strategies, publications, postCount, activeAuthorId]);

  const counts = useMemo(() => gatherContentPlanReadinessCounts(sources), [sources]);
  const readiness = useMemo(() => computeContentPlanFlowReadiness(sources), [sources]);

  const loading = authorsLoading
    || platformsLoading
    || pillarsLoading
    || topicsLoading
    || strategiesLoading
    || (Boolean(newestStrategyId) && publicationsLoading)
    || postsLoading;

  return { readiness, counts, effectiveAuthorId, loading, refresh };
}
